// @flow
import { Record, List, Map } from "immutable"
import * as R from "ramda"

import { PLACE_MODES, PLACE_VALUE_MODES } from "../consts/place"
import SearchPlace from "./SearchPlace"
import type { Plain } from "./SearchPlace"
import type Place from "./Place"

export const ANYWHERE = "anywhere"

type InputObject = {
  places?: List<SearchPlace>,
}

type MultiPlain = {
  places?: Array<Plain>,
}

const MultiPlaceRecord = Record({
  places: List(),
})

class MultiPlace extends MultiPlaceRecord {
  constructor(inputObject: InputObject = {}) {
    // let's group places even when opening page with url params
    if (inputObject.places) {
      // eslint-disable-next-line no-param-reassign
      inputObject.places = R.uniqBy(t => t.getId(), inputObject.places.toArray())
        // add new and remove all bigger places it is included in
        .reduce(
          (acc, place) => acc.filter(t => !place.hasParent(t) && !t.hasParent(place)).push(place),
          new List(),
        )
    }
    super(inputObject)
  }

  groupPlaces(newPosition: number): MultiPlace {
    const addedPlace = this.places.get(newPosition)
    return this.update("places", places =>
      places.filter(
        (place, i) =>
          i === newPosition || (!addedPlace.hasParent(place) && !place.hasParent(addedPlace)),
      ),
    )
  }

  sortPlaces() {
    return this.set("places", this.places.sortBy(item => item.getSearchId()))
  }

  addPlace(place: SearchPlace): MultiPlace {
    if (this.contains(place)) {
      return this
    }

    if (
      (place.value && place.value.id === ANYWHERE) ||
      (this.places.size > 0 && this.places.first().mode === PLACE_MODES.ANYWHERE)
    ) {
      return this.set("places", List([place]))
    }
    return this.set("places", this.places.push(place)).groupPlaces(Math.max(this.places.size, 0))
  }

  removePlace(searchPlace: SearchPlace): MultiPlace {
    return this.update("places", places => places.delete(places.indexOf(searchPlace)))
  }

  updatePlace(oldPlace: SearchPlace, newPlace: SearchPlace) {
    return this.setIn(["places", this.places.indexOf(oldPlace)], newPlace)
  }

  addPlaceAtPosition(position: number, place: SearchPlace) {
    if (this.contains(place)) return this

    if (
      place.mode === PLACE_MODES.ANYWHERE ||
      (this.places.size > 0 && this.places.first().mode === PLACE_MODES.ANYWHERE)
    ) {
      return this.set("places", List([place]))
    }

    return this.set("places", this.places.insert(position, place)).groupPlaces(position)
  }

  updatePlaceAtPosition(position: number, place: SearchPlace) {
    if (position === -1) {
      return this.addPlace(place)
    }
    if (position === this.places.size) {
      return this.set("places", List([place]))
    }

    return this.set("places", this.places.set(position, place)).groupPlaces(position)
  }

  deletePlaceAtPosition(position: number) {
    if (position === this.places.size) {
      return this.set("places", List())
    }
    return this.places.size > 0 ? this.set("places", this.places.delete(position)) : this
  }

  isDefault(): boolean {
    return this.places.size === 1 && this.places.first() && this.places.first().isDefault
  }

  setNotDefault(): MultiPlace {
    return this.isDefault()
      ? this.set("places", this.places.set(0, this.places.first().set("isDefault", false)))
      : this
  }

  getType(): string {
    if (this.places.size > 1) {
      return "multiPlace"
    }

    if (this.places.size === 1 && this.places.first()) {
      return this.places.first().getType()
    }

    return "place"
  }

  getId(): string {
    if (this.places.size > 1) {
      return "multiPlace"
    }

    if (this.places.size === 1 && this.places.first()) {
      return this.places.first().getId()
    }

    return "-"
  }

  getIds(): string {
    if (this.isDefault()) {
      return "--"
    }
    if (this.places.size > 0) {
      return this.places.map(place => place.getId()).join(",")
    }
    return "-"
  }

  getPlaceIds(): string {
    if (this.places.size) {
      return this.places.map(place => place.getId()).join(",")
    }
    return "-"
  }

  getSearchId(): ?string {
    if (this.places.size > 0) {
      if (this.places.some(place => place.getId() === PLACE_MODES.ANYWHERE)) {
        return PLACE_MODES.ANYWHERE
      }
      return this.places.map(place => place.getSearchId()).join(",")
    }
    return PLACE_MODES.ANYWHERE
  }

  getMode(): ?string {
    if (this.places.size > 0) {
      if (this.places.size === 1 && this.places.first()) {
        return this.places.first().mode
      }
      return PLACE_VALUE_MODES.MULTI_PLACE
    }
    return PLACE_VALUE_MODES.UNSELECTED
  }

  isSimple(): boolean {
    return this.places.size === 1 && this.places.get(0).isSimple()
  }

  containsSimpleOnly(): boolean {
    return this.places.size > 0 && this.places.every(place => place.isSimple())
  }

  isLarge(): boolean {
    if (this.places.size === 0) {
      return false
    }
    return this.places.some(place => place.isLarge())
  }

  isEmpty(): boolean {
    return this.places.isEmpty()
  }

  isAnywhere(): boolean {
    return this.places.size === 1 && this.places.first().isAnywhere()
  }

  static fromPlainJS(plain: MultiPlain): MultiPlace {
    return new MultiPlace({
      places: plain.places
        ? List(plain.places.map(plainPlace => SearchPlace.fromPlainJS(plainPlace)))
        : List(),
    })
  }

  getGeo() {
    return this.places.size === 1 ? this.places.get(0).getGeo() : null
  }

  contains(place: SearchPlace) {
    return this.places.some(p => p.equals(place))
  }

  containsRadius() {
    return this.places.some(p => p.mode === PLACE_MODES.RADIUS)
  }

  containsDeep(place: Place, places: Map<string, Place>) {
    return this.places.some(p =>
      p
        .getPlaces(places)
        .map(i => i.get("id"))
        .includes(place.getCityId()),
    )
  }

  getPlaceCodeList(): string {
    return this.places
      .map(place => place.getPlaceValue() && place.getPlaceValue().get("code"))
      .join(",")
  }

  getPlaceCodeSingle(): string {
    return (
      this.places.first() &&
      this.places.first().getPlaceValue() &&
      this.places
        .first()
        .getPlaceValue()
        .get("code")
    )
  }

  removeSpecial() {
    return this.set("places", this.places.filter(place => !place.isSpecial()))
  }

  static anywhere() {
    return new MultiPlace({ places: List([new SearchPlace({ mode: "anywhere" })]) })
  }

  static empty() {
    return new MultiPlace({ places: List() })
  }
}

export const createMultiplaceFromPlaces = (places: Place[]) =>
  new MultiPlace({
    places: new List(
      places.map(place => new SearchPlace({ mode: PLACE_VALUE_MODES.PLACE, value: place })),
    ),
  })

export default MultiPlace
